import { useState, useEffect } from "react";
import { useAuth } from "../utils/useAuth";
import { DraftList, InvoicePosition } from "../types/invoice";
import type { Client } from "../types/client";
import BaseForm from "./invoicePositions/BaseForm";
import PercentForm from "./invoicePositions/PercentForm";
import AdjustmentForm from "./invoicePositions/AdjustmentForm";
import AdjustmentPercentForm from "./invoicePositions/AdjustmentPercentForm";
import RevenuePercentForm from "./invoicePositions/RevenuePercentForm";
import BudgetForm from "./invoicePositions/BudgetForm";
import ClientSearch from "./ClientSearch";
import { previewInvoices } from "../api/invoices";
import { colors, spacing, borderRadius, typography, styles, createButtonStyle } from "../styles/theme";

// Walidator pozycji (front + UX info)
function validatePosition(pos: InvoicePosition): string | null {
  if (pos.type === "base") {
    if (!pos.amount_netto || pos.amount_netto <= 0) return "Podstawa: Kwota netto musi być > 0";
  }
  if (pos.type === "percent") {
    if (!pos.nickname || !pos.date_from || !pos.date_to)
      return "Procent: Uzupełnij nickname oraz daty od–do";
    if (!pos.percent || pos.percent <= 0) return "Procent: Wartość procentowa musi być > 0";
    if ((pos as any).threshold != null && (pos as any).threshold < 0)
      return "Procent: Próg (PLN) nie może być ujemny";
  }
  if (pos.type === "adjustment") {
    if (!pos.date_from || !pos.date_to)
      return "Wyrównanie: Uzupełnij daty od–do";
    if (!pos.podstawa_do_wyrownania || pos.podstawa_do_wyrownania <= 0)
      return "Wyrównanie: Podstawa do wyrównania musi być > 0";
  }
  if (pos.type === "adjustment_percent") {
    if (!pos.client_id || !pos.date_from || !pos.date_to)
      return "Wyrównanie %: Uzupełnij klienta oraz daty od–do";
    if (!pos.percent || pos.percent <= 0) return "Wyrównanie %: Wartość procentowa musi być > 0";
    if ((pos as any).threshold != null && (pos as any).threshold < 0)
      return "Wyrównanie %: Próg (PLN) nie może być ujemny";
  }
  if (pos.type === "revenue_percent") {
    if (!pos.customer_id || !pos.date_from || !pos.date_to)
      return "% od obrotu: Uzupełnij klienta oraz daty od–do";
    if (!pos.percent || pos.percent <= 0) return "% od obrotu: Wartość procentowa musi być > 0";
    if ((pos as any).minimum != null && (pos as any).minimum < 0)
      return "% od obrotu: Minimum (PLN) nie może być ujemne";
  }
  if (pos.type === "budget") {
    if (!pos.customer_id || !pos.date_from || !pos.date_to)
      return "Budżet: Uzupełnij klienta oraz daty od–do";
  }
  return null;
}

/** <<< NOWE >>> — czyścimy payload do /invoices/preview */
function sanitizeForPreview(draft: DraftList): DraftList {
  return draft.map(inv => ({
    ...inv,
    positions: inv.positions.map((p) => {
      if (p.type === "percent") {
        const { client_id, anchor_start, month_key, ...rest } = p as any;
        const out: any = { ...rest };
        // wysyłamy tylko, gdy realnie ustawione:
        if (anchor_start) out.anchor_start = anchor_start;
        if (month_key) out.month_key = month_key;
        return out;
      }
      if (p.type === "revenue_percent") {
        const { anchor_start, month_key, ...rest } = p as any;
        const out: any = { ...rest };
        // wysyłamy tylko, gdy realnie ustawione:
        if (anchor_start) out.anchor_start = anchor_start;
        if (month_key) out.month_key = month_key;
        return out;
      }
      if (p.type === "budget") {
        const { anchor_start, month_key, ...rest } = p as any;
        const out: any = { ...rest };
        // wysyłamy tylko, gdy realnie ustawione:
        if (anchor_start) out.anchor_start = anchor_start;
        if (month_key) out.month_key = month_key;
        return out;
      }
      return p;
    })
  }));
}

const DashboardUser = () => {
  const { user } = useAuth();

  // Draft (lista faktur)
  const [draft, setDraft] = useState<DraftList>(() => {
    const stored = localStorage.getItem("draft_preview");
    return stored ? JSON.parse(stored) : [];
  });

  // Wybrany klient Fakturowni
  const [selectedClient, setSelectedClient] = useState<Client | null>(() => {
    const stored = localStorage.getItem("selected_client");
    return stored ? JSON.parse(stored) : null;
  });

  // NOWE: Wybrana firma (Semtop/AdTarget)
  const [selectedCompany, setSelectedCompany] = useState<"semtop" | "adtarget" | null>(() => {
    const stored = localStorage.getItem("selected_company");
    return stored ? JSON.parse(stored) : null;
  });

  // zapisz draft w LS
  useEffect(() => {
    localStorage.setItem("draft_preview", JSON.stringify(draft));
  }, [draft]);

  // zapisz firmę w LS
  useEffect(() => {
    if (selectedCompany) {
      localStorage.setItem("selected_company", JSON.stringify(selectedCompany));
    }
  }, [selectedCompany]);

  const [openForm, setOpenForm] = useState<{
    invoiceIdx: number;
    type: "base" | "percent" | "adjustment" | "revenue_percent" | "budget";
  } | null>(null);

  // Zmiana klienta = ustawienie dla każdej faktury w drafcie
  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    localStorage.setItem("selected_client", JSON.stringify(client));
    setDraft(d => d.map(inv => ({ ...inv, client_id: client.id })));
  };

  // Dodanie nowej pustej faktury
  const handleAddInvoice = () => {
    if (!selectedClient || !selectedCompany) return;
    setDraft(d => [...d, {
      client_id: selectedClient.id,
      type: "proforma",
      department_name: selectedCompany,  // NOWE
      positions: []
    }]);
  };

  // Dodanie pozycji do faktury
  const handleAddPosition = (invoiceIdx: number, position: InvoicePosition) => {
    setDraft(d =>
      d.map((inv, idx) => (idx === invoiceIdx ? { ...inv, positions: [...inv.positions, position] } : inv))
    );
  };

  // Walidacje
  const invalids = draft.flatMap((inv, i) =>
    inv.positions.map((pos, j) => ({ msg: validatePosition(pos), i, j })).filter(v => v.msg)
  );
  const hasIncompletePositions = invalids.length > 0;

  // Scroll do pierwszego błędu
  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector(".invoice-position-error");
      if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
  };

  return (
    <div style={styles.container}>
      <div style={{ marginBottom: spacing.xxl }}>
        <h2 style={{ color: colors.textPrimary, marginBottom: spacing.sm }}>👋 Witaj, {user?.email}</h2>
        <h3 style={{ color: colors.textSecondary, fontWeight: typography.fontWeight.normal, marginTop: 0 }}>Kreator faktur</h3>
      </div>

      {/* NOWE: Wybór firmy */}
      <div style={{ ...styles.section, marginBottom: spacing.xl }}>
        <h4 style={{ marginTop: 0, marginBottom: spacing.md }}>Wybierz firmę</h4>
        <div style={{ display: 'flex', gap: spacing.md }}>
          <button
            onClick={() => setSelectedCompany('semtop')}
            style={{
              ...createButtonStyle(selectedCompany === 'semtop' ? 'primary' : 'secondary'),
              flex: 1,
            }}
          >
            {selectedCompany === 'semtop' ? '✓ ' : ''}Semtop
          </button>
          <button
            onClick={() => setSelectedCompany('adtarget')}
            style={{
              ...createButtonStyle(selectedCompany === 'adtarget' ? 'primary' : 'secondary'),
              flex: 1,
            }}
          >
            {selectedCompany === 'adtarget' ? '✓ ' : ''}AdTarget
          </button>
        </div>
        {!selectedCompany && (
          <div style={{
            color: colors.errorDark,
            background: colors.errorLight,
            padding: spacing.md,
            borderRadius: borderRadius.sm,
            marginTop: spacing.md,
            border: `1px solid ${colors.error}`,
          }}>
            ⚠️ Wybierz firmę, aby rozpocząć!
          </div>
        )}
      </div>

      {/* Wybór klienta */}
      <div style={{ ...styles.section, marginBottom: spacing.xl }}>
        <h4 style={{ marginTop: 0, marginBottom: spacing.md }}>📋 Wybierz klienta</h4>
        <ClientSearch onSelect={handleClientSelect} selected={selectedClient} />
        {!selectedClient && (
          <div style={{
            color: colors.errorDark,
            background: colors.errorLight,
            padding: spacing.md,
            borderRadius: borderRadius.sm,
            marginTop: spacing.md,
            border: `1px solid ${colors.error}`,
          }}>
            ⚠️ Wybierz klienta, aby rozpocząć wystawianie faktur!
          </div>
        )}
        {selectedClient && (
          <div style={{
            color: colors.successDark,
            background: colors.successLight,
            padding: spacing.md,
            borderRadius: borderRadius.sm,
            marginTop: spacing.md,
            border: `1px solid ${colors.success}`,
          }}>
            ✅ Wybrany klient: <strong>{selectedClient.name}</strong>
            {selectedClient.shortcut && <span style={{ color: colors.textSecondary }}> [{selectedClient.shortcut}]</span>}
            {selectedClient.email && <span style={{ color: colors.textSecondary }}> – {selectedClient.email}</span>}
          </div>
        )}
      </div>

      <button
        onClick={handleAddInvoice}
        disabled={!selectedClient || !selectedCompany}
        style={{
          ...createButtonStyle('primary', !selectedClient || !selectedCompany),
          marginBottom: spacing.lg,
        }}
      >
        ➕ Dodaj nową fakturę
      </button>

      {draft.length === 0 && selectedClient && (
        <div style={{
          textAlign: 'center',
          padding: spacing.xxl,
          color: colors.textSecondary,
          fontSize: typography.fontSize.md,
        }}>
          Brak faktur w drafcie. Kliknij "Dodaj nową fakturę" aby rozpocząć.
        </div>
      )}

      {hasIncompletePositions && (
        <div style={{
          color: colors.errorDark,
          background: colors.errorLight,
          padding: spacing.md,
          borderRadius: borderRadius.sm,
          marginBottom: spacing.lg,
          border: `1px solid ${colors.error}`,
          fontWeight: typography.fontWeight.semibold,
        }}>
          ❌ Popraw błędy w pozycjach (oznaczone na czerwono poniżej)
        </div>
      )}

      {draft.map((invoice, idx) => (
        <div key={idx} style={{
          ...styles.card,
          marginBottom: spacing.lg,
          background: colors.background,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: spacing.md }}>
            <h4 style={{ margin: 0, color: colors.textPrimary }}>📄 Faktura {idx + 1}</h4>
            <button
              style={createButtonStyle('danger')}
              onClick={() => setDraft(d => d.filter((_, i) => i !== idx))}
            >
              🗑️ Usuń fakturę
            </button>
          </div>

          <div style={{ marginBottom: spacing.md }}>
            <label style={{ fontWeight: typography.fontWeight.medium, marginRight: spacing.sm }}>Typ faktury:</label>
            <select
              value={invoice.type}
              onChange={e => {
                const val = e.target.value as "proforma" | "vat";
                setDraft(d => d.map((inv, i) => (i === idx ? { ...inv, type: val } : inv)));
              }}
              style={styles.select}
            >
              <option value="proforma">Proforma</option>
              <option value="vat">VAT</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.md }}>
            <button onClick={() => setOpenForm({ invoiceIdx: idx, type: "base" })} style={createButtonStyle('secondary')}>➕ Podstawa</button>
            <button onClick={() => setOpenForm({ invoiceIdx: idx, type: "percent" })} style={createButtonStyle('secondary')}>➕ Procent</button>
            <button onClick={() => setOpenForm({ invoiceIdx: idx, type: "adjustment" })} style={createButtonStyle('secondary')}>➕ Wyrównanie</button>
            <button onClick={() => setOpenForm({ invoiceIdx: idx, type: "adjustment_percent" })} style={createButtonStyle('secondary')}>➕ Wyrównanie %</button>
            <button onClick={() => setOpenForm({ invoiceIdx: idx, type: "revenue_percent" })} style={createButtonStyle('secondary')}>➕ % od obrotu</button>
            <button onClick={() => setOpenForm({ invoiceIdx: idx, type: "budget" })} style={createButtonStyle('secondary')}>➕ Budżet</button>
          </div>

          {openForm && openForm.invoiceIdx === idx && (
            openForm.type === "base" ? (
              <BaseForm onAdd={pos => { handleAddPosition(idx, pos); setOpenForm(null); }} onCancel={() => setOpenForm(null)} />
            ) : openForm.type === "percent" ? (
              <PercentForm onAdd={pos => { handleAddPosition(idx, pos); setOpenForm(null); }} onCancel={() => setOpenForm(null)} />
            ) : openForm.type === "adjustment" ? (
              <AdjustmentForm onAdd={pos => { handleAddPosition(idx, pos); setOpenForm(null); }} onCancel={() => setOpenForm(null)} />
            ) : openForm.type === "adjustment_percent" ? (
              <AdjustmentPercentForm onAdd={pos => { handleAddPosition(idx, pos); setOpenForm(null); }} onCancel={() => setOpenForm(null)} />
            ) : openForm.type === "revenue_percent" ? (
              <RevenuePercentForm onAdd={pos => { handleAddPosition(idx, pos); setOpenForm(null); }} onCancel={() => setOpenForm(null)} />
            ) : (
              <BudgetForm onAdd={pos => { handleAddPosition(idx, pos); setOpenForm(null); }} onCancel={() => setOpenForm(null)} />
            )
          )}

          <div style={{ marginTop: spacing.md }}>
            {invoice.positions.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: spacing.lg,
                color: colors.textSecondary,
                background: colors.gray50,
                borderRadius: borderRadius.sm,
                fontSize: typography.fontSize.sm,
              }}>
                Brak pozycji. Dodaj pozycję używając przycisków powyżej.
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {invoice.positions.map((pos, pidx) => {
                  const errorMsg = validatePosition(pos);
                  return (
                    <li key={pidx} style={{
                      background: errorMsg ? colors.errorLight : colors.gray50,
                      border: `1px solid ${errorMsg ? colors.error : colors.borderLight}`,
                      borderRadius: borderRadius.sm,
                      padding: spacing.md,
                      marginBottom: spacing.sm,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <div style={{ flex: 1 }}>
                        {pos.type === "base" && <>{pos.name || "Podstawa"}: {pos.amount_netto} zł {pos.description && `(${pos.description})`}</>}
                        {pos.type === "percent" && <>{pos.name || `Procent ${pos.percent}%`}: {pos.nickname} {typeof (pos as any).threshold === "number" ? ` | Próg: ${(pos as any).threshold} PLN` : ""} {pos.description && `(${pos.description})`}</>}
                        {pos.type === "adjustment" && <>{pos.name || "Wyrównanie"}: podstawa {pos.podstawa_do_wyrownania} zł {pos.description && `(${pos.description})`}</>}
                        {pos.type === "adjustment_percent" && <>{pos.name || `Wyrównanie ${pos.percent}%`}: {pos.client_id} <span style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginLeft: spacing.xs }}>[{pos.source_type === "allegro" ? "Allegro Ads" : "Google Ads"}]</span> {typeof pos.threshold === "number" ? ` | Próg: ${pos.threshold} PLN` : ""} {pos.description && `(${pos.description})`}</>}
                        {pos.type === "revenue_percent" && <>{pos.name || `${pos.percent}% od obrotu`}: {pos.descriptive_name} <span style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginLeft: spacing.xs }}>[{(pos as any).source_type === "allegro" ? "Allegro Ads" : "Google Ads"}]</span> {typeof (pos as any).minimum === "number" ? ` | Min: ${(pos as any).minimum} PLN` : ""} {pos.description && `(${pos.description})`}</>}
                        {pos.type === "budget" && (() => {
                          const budgetPos = pos as any;
                          const formatDate = (isoDate: string) => {
                            const [y, m, d] = isoDate.split("-");
                            return `${d}.${m}.${y}`;
                          };
                          const dateFromFormatted = budgetPos.date_from ? formatDate(budgetPos.date_from) : "—";
                          const dateToFormatted = budgetPos.date_to ? formatDate(budgetPos.date_to) : "—";
                          const sourceLabel = budgetPos.source_type === "allegro" ? "Allegro Ads" : "Google Ads";

                          return (
                            <>
                              Budżet za okres {dateFromFormatted} - {dateToFormatted}: {pos.descriptive_name} <span style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>[{sourceLabel}]</span>
                            </>
                          );
                        })()}
                        {errorMsg && (
                          <div className="invoice-position-error" style={{
                            color: colors.errorDark,
                            marginTop: spacing.xs,
                            fontWeight: typography.fontWeight.semibold,
                            fontSize: typography.fontSize.sm,
                          }}>
                            ⚠️ {errorMsg}
                          </div>
                        )}
                      </div>
                      <button
                        style={{
                          ...createButtonStyle('danger'),
                          marginLeft: spacing.md,
                          padding: `${spacing.xs}px ${spacing.md}px`,
                          fontSize: typography.fontSize.sm,
                        }}
                        onClick={() => setDraft(d => d.map((inv, i) =>
                          i === idx ? { ...inv, positions: inv.positions.filter((_, j) => j !== pidx) } : inv))}
                      >
                        🗑️ Usuń
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      ))}

      {selectedClient && draft.length > 0 && (
        <div style={{ marginTop: spacing.xxl, textAlign: 'center' }}>
          <button
            style={{
              ...createButtonStyle('success', hasIncompletePositions),
              fontSize: typography.fontSize.md,
              padding: `${spacing.md}px ${spacing.xxl}px`,
            }}
            disabled={hasIncompletePositions}
            title={hasIncompletePositions ? "Uzupełnij wszystkie pozycje przed podglądem" : ""}
            onClick={async () => {
              if (hasIncompletePositions) { scrollToFirstError(); return; }
              try {
                const sanitized = sanitizeForPreview(draft);
                const result = await previewInvoices(sanitized);
                localStorage.setItem("preview_result", JSON.stringify({
                  ...result,
                  company: selectedCompany  // NOWE
                }));
                window.location.href = "/preview";
              } catch (e: any) {
                if (e?.response?.status === 404) {
                  alert("Brak danych dla wybranego zakresu lub brak pozycji w bazie.");
                } else {
                  alert("Błąd generowania podglądu faktur!");
                }
              }
            }}
          >
            👁️ Przejdź do podglądu
          </button>
          {hasIncompletePositions && (
            <div style={{
              color: colors.errorDark,
              marginTop: spacing.md,
              fontSize: typography.fontSize.sm,
            }}>
              ⚠️ Popraw błędy powyżej przed podglądem
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardUser;
